/**
 * How far an element has travelled through the page's viewport, as 0..1, and a
 * composable that keeps a ref of it for the page's own scroller.
 *
 * Owns: the arithmetic (`progressThrough`, pure) and one passive scroll
 * listener per use, throttled to a frame. Must not: write to the DOM; callers
 * decide what progress draws. Under reduced motion progress is 1 at rest, so
 * a ladder that fills with scroll is simply full.
 */
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

import { reducedMotion } from '@/engine/motion';

/**
 * 0 while the element's top is at `start` (a fraction of the viewport height
 * from the top; 0.85 is near the bottom edge) and 1 once its bottom has
 * reached `end`; linear between, clamped outside.
 */
export function progressThrough(
  rect: { top: number; height: number },
  viewportHeight: number,
  start = 0.85,
  end = 0.55,
): number {
  const travel = rect.height + viewportHeight * (start - end);
  if (travel <= 0) return 1;
  return Math.min(1, Math.max(0, (viewportHeight * start - rect.top) / travel));
}

export function useScrollProgress(
  getScroller: () => HTMLElement | null,
  getTarget: () => HTMLElement | null,
  start?: number,
  end?: number,
): Ref<number> {
  const progress = ref(reducedMotion() ? 1 : 0);
  let scroller: HTMLElement | null = null;
  let raf = 0;

  function measure(): void {
    raf = 0;
    const el = getTarget();
    if (!el || !scroller) return;
    progress.value = progressThrough(el.getBoundingClientRect(), scroller.clientHeight, start, end);
  }

  function onScroll(): void {
    if (!raf) raf = requestAnimationFrame(measure);
  }

  onMounted(() => {
    if (reducedMotion()) return;
    scroller = getScroller();
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    measure();
  });

  onBeforeUnmount(() => {
    scroller?.removeEventListener('scroll', onScroll);
    if (raf) cancelAnimationFrame(raf);
  });

  return progress;
}
